/**
 * @module
 * @description To be used with store/locale; returns a vue-i18n instance setup with store watchers to change locale, scans @/locales/ for available locales.
 * @param {string} VUE_APP_I18N_LOCALE process.env.VUE_APP_I18N_LOCALE, default locale when no matching locale is found
 * @example
 * import i18n from '@tokenyico/vuejs-toolkit/src/exports/i18n';
 * import store from '@/store';
 *
 * window.app = new Vue({
 *   i18n: i18n(store, {
 *     dateTimeFormats: {
 *       en: {
 *         short: {
 *           year: 'numeric', month: 'short', day: 'numeric'
 *         }
 *       }
 *     }
 *   }),
 * // other Vue construction options
 * }).$mount('#app');
 */
import Vue from 'vue';
import VueI18n from 'vue-i18n';
Vue.use(VueI18n);

export const defaultLocale = process.env.VUE_APP_I18N_LOCALE;
export const locales = [];

/**
 * @function default
 * @param {object} store Vuex store instance
 * @param {object?} i18nArgs Additional key/values to send to i18n constructor
 * @returns {object} vue-i18n instance
 */
export default (store, i18nArgs = {}) => {
  const messages = {};
  const webpackedLocales = require.context('@/locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
  for (const key of webpackedLocales.keys()) {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    /* istanbul ignore else */
    if (matched && matched.length > 1) {
      const locale = matched[1];
      messages[locale] = webpackedLocales(key);
      locales.push(locale);
    }
  }

  let locale = defaultLocale;
  if (store.state.locale.locale !== null && locales.includes(store.state.locale.locale)) {
    // store locale exists and is in the list of locales
    locale = store.state.locale.locale;
  } else {
    if (window.navigator.language && locales.includes(window.navigator.language)) {
      // navigator locale is in list locales
      locale = window.navigator.language;
    } else {
      const localeLangs = locales.map(l => l.split('-', 2)[0]); // take part before '-'
      for (const lang of window.navigator.languages) {
        const localeIndex = localeLangs.findIndex(l => l === lang);
        if (localeIndex !== -1) {
          // matching lang in locale list
          locale = locales[localeIndex];
          break;
        }
      }
    }
    store.dispatch('locale/setLocale', locale);
  }

  const i18n = new VueI18n({
    ...i18nArgs,
    locale,
    messages
  });

  store.watch((state) => state.locale.locale, (newLocale) => {
    i18n.locale = newLocale;
  });

  return i18n;
};
